import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import type { StoH4Position, StoH4Status } from '@contracts/stoH4';
import { FeedBanner } from '../components/FeedBanner';
import { Banner, Empty, Panel } from '../components/Panel';
import { DirectionChip } from '../components/SignalPanel';
import { useFeedView } from '../hooks/useFeedView';
import { api } from '../lib/api';
import { dateTime, price } from '../lib/format';

const PER_PAGE = 10;

export function usd(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  return `${v >= 0 ? '+' : '−'}$${Math.abs(v).toFixed(2)}`;
}

function tone(v: number | null | undefined): string {
  return v === null || v === undefined ? '' : v >= 0 ? 'pos' : 'neg';
}

function days(d: number | undefined): string {
  return d === undefined ? '—' : `${d.toFixed(1)} วัน`;
}

const STATE_TEXT: Record<StoH4Status['state'], string> = {
  WAIT: 'รอสัญญาณ — Stoch H4 อยู่ระหว่าง 20–80',
  NEAR_BUY: 'Stoch H4 อยู่ใต้ 20 — รอตัดขึ้น (BUY) พร้อม H1 ยืนยัน',
  NEAR_SELL: 'Stoch H4 อยู่เหนือ 80 — รอตัดลง (SELL) พร้อม H1 ยืนยัน',
};

function heldDays(p: StoH4Position): number {
  if (!p.closedAt) return p.heldDays ?? 0;
  return (new Date(p.closedAt).getTime() - new Date(p.createdAt).getTime()) / 86_400_000;
}

/** Sto-H4: a separate alert system (H4 Stochastic + H1 confirmation, no SL / TP). Alerts only, no orders. */
export function StoH4Page() {
  const view = useFeedView();
  const statusQ = useQuery({
    queryKey: ['sto-h4-status'],
    queryFn: () => api.get<StoH4Status>('/api/v1/sto-h4/status'),
    refetchInterval: 15_000,
  });
  const closedQ = useQuery({
    queryKey: ['sto-h4-closed'],
    queryFn: () => api.get<{ positions: StoH4Position[] }>('/api/v1/sto-h4/closed?limit=500'),
    refetchInterval: 60_000,
  });
  const [page, setPage] = useState(1);
  const s = statusQ.data;
  const closed = closedQ.data?.positions ?? [];
  const pages = Math.max(1, Math.ceil(closed.length / PER_PAGE));
  const current = Math.min(page, pages);
  const shown = closed.slice((current - 1) * PER_PAGE, current * PER_PAGE);
  const ch = s?.channels;

  return (
    <>
      <header className="page__header">
        <h1>Sto-H4</h1>
        <p className="page__lede">
          ระบบแจ้งเตือนแยกต่างหาก: Stoch (5,3,3) H4 ตัดขึ้นจากใต้ 20 / ตัดลงจากเหนือ 80 + Stoch H1 ยืนยัน ·
          ทุกสัญญาณเปิดไม้ใหม่ 0.01 lot · ไม่มี SL/TP · ปิดกำไรเมื่อ Stoch H4 ถึงฝั่งตรงข้ามและไม้นั้นกำไร ·{' '}
          <Link to="/guide/sto-h4">อ่านคู่มือ Sto-H4</Link>
        </p>
      </header>
      <FeedBanner view={view} />
      <Banner tone="warn" title="ระบบนี้ไม่มี Stop Loss">
        ไม้ที่ผิดทางจะถูกถือค้างจนกว่าจะกลับมากำไร · ผลทดสอบย้อนหลัง 2 ปี ไม้ค้างติดลบรวมลึกสุด −$48,124 · สุทธิ ≈
        −$7,320 (ต่อไม้ 0.01 lot) · เป็นการแจ้งเตือนเท่านั้น ไม่ใช่คำแนะนำการลงทุน
      </Banner>

      <div className="stack" style={{ marginTop: 16 }}>
        <Panel title="สถานะ" meta={s ? `H4 ปิดครั้งถัดไป ${dateTime(s.nextH4Close)}` : undefined}>
          {statusQ.isError ? (
            <Empty title="โหลดสถานะไม่สำเร็จ">ติดต่อ backend ไม่ได้</Empty>
          ) : !s ? (
            <Empty title="กำลังโหลด…" />
          ) : (
            <>
              <div className="row" style={{ flexWrap: 'wrap', gap: 16 }}>
                <span>
                  Stoch H4: <strong data-testid="k4">%K {s.h4.k?.toFixed(1) ?? '—'}</strong> / %D{' '}
                  {s.h4.d?.toFixed(1) ?? '—'}
                </span>
                <span>
                  Stoch H1: <strong>%K {s.h1K?.toFixed(1) ?? '—'}</strong>
                </span>
                <span>Bid {price(s.bid)}</span>
              </div>
              <p style={{ margin: '8px 0' }}>
                <span aria-hidden="true">⏳</span> {STATE_TEXT[s.state]}
              </p>
              {s.lastEvaluation?.skipped ? (
                <p className="small neg" style={{ margin: 0 }}>
                  แท่ง H4 ล่าสุดไม่ได้ตรวจ ({s.lastEvaluation.skipped}) — ข้อมูลราคาไม่พร้อม
                </p>
              ) : null}
              <p className="small muted" style={{ marginBottom: 0 }}>
                ช่องทางแจ้งเตือน: เว็บ ✓ · แอป (Web Push) {ch?.push ? '✓' : 'ปิด'} · LINE {ch?.line ? '✓' : 'ปิด'} ·
                เริ่มนับไม้ตั้งแต่ {dateTime(s.startedAt)}
              </p>
            </>
          )}
        </Panel>

        <Panel title="สรุป" meta="ต่อไม้ 0.01 lot · แสดงกำไรที่ปิดแล้วคู่กับไม้ค้างเสมอ">
          {s ? (
            <div className="grid grid--2" data-testid="totals">
              <p style={{ margin: 0 }}>
                ปิดกำไรแล้ว {s.totals.closedCount} ไม้{' '}
                <strong className={tone(s.totals.realized)}>{usd(s.totals.realized)}</strong>
              </p>
              <p style={{ margin: 0 }}>
                เปิดค้าง {s.totals.openCount} ไม้ ลอยอยู่{' '}
                <strong className={tone(s.totals.floating)}>{usd(s.totals.floating)}</strong>
              </p>
              <p style={{ margin: 0 }}>
                สุทธิถ้าปิดตอนนี้ <strong className={tone(s.totals.net)}>{usd(s.totals.net)}</strong>
              </p>
              <p style={{ margin: 0 }}>
                ไม้ค้างติดลบลึกสุด <strong className={tone(s.totals.worstOpen)}>{usd(s.totals.worstOpen)}</strong>
              </p>
            </div>
          ) : (
            <Empty title="กำลังโหลด…" />
          )}
        </Panel>

        <Panel title="ไม้ที่เปิดอยู่" meta={s ? `${s.open.length} ไม้` : undefined}>
          {!s || s.open.length === 0 ? (
            <Empty title="ยังไม่มีไม้เปิด">ไม้แรกจะเกิดเมื่อแท่ง H4 ปิดพร้อมสัญญาณครบเงื่อนไข</Empty>
          ) : (
            <div className="table-wrap">
              <table className="ledger">
                <caption className="visually-hidden">ไม้ Sto-H4 ที่เปิดอยู่</caption>
                <thead>
                  <tr>
                    <th>ไม้</th>
                    <th>เข้าเมื่อ</th>
                    <th>สัญญาณ</th>
                    <th className="r">ราคาเข้า</th>
                    <th className="r">ลอยตอนนี้</th>
                    <th className="r">ติดลบลึกสุด</th>
                    <th className="r">ถือมา</th>
                  </tr>
                </thead>
                <tbody>
                  {s.open.map((p) => (
                    <tr key={p.id}>
                      <td>#{p.number}</td>
                      <td>{dateTime(p.createdAt)}</td>
                      <td>
                        <DirectionChip direction={p.direction} />
                      </td>
                      <td className="r">{price(p.entry)}</td>
                      <td className={`r ${tone(p.floating)}`}>{usd(p.floating)}</td>
                      <td className={`r ${tone(p.worst)}`}>{usd(p.worst)}</td>
                      <td className="r">{days(p.heldDays)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>

        <Panel
          title="ประวัติไม้ที่ปิดแล้ว"
          meta={`${closed.length} ไม้${pages > 1 ? ` · หน้า ${current}/${pages}` : ''}`}
        >
          {closed.length === 0 ? (
            <Empty title={closedQ.isLoading ? 'กำลังโหลด…' : 'ยังไม่มีไม้ที่ปิด'} />
          ) : (
            <>
              <div className="table-wrap">
                <table className="ledger">
                  <caption className="visually-hidden">ไม้ Sto-H4 ที่ปิดแล้ว ใหม่สุดก่อน</caption>
                  <thead>
                    <tr>
                      <th>ไม้</th>
                      <th>เข้า</th>
                      <th>ออก</th>
                      <th>สัญญาณ</th>
                      <th className="r">ราคาเข้า → ออก</th>
                      <th className="r">กำไร</th>
                      <th className="r">ถือ</th>
                      <th className="r">ติดลบลึกสุด</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shown.map((p) => (
                      <tr key={p.id}>
                        <td>#{p.number}</td>
                        <td>{dateTime(p.createdAt)}</td>
                        <td>{dateTime(p.closedAt)}</td>
                        <td>
                          <DirectionChip direction={p.direction} />
                        </td>
                        <td className="r">
                          {price(p.entry)} → {price(p.exitPrice)}
                        </td>
                        <td className={`r ${tone(p.pnl)}`}>{usd(p.pnl)}</td>
                        <td className="r">{days(heldDays(p))}</td>
                        <td className={`r ${tone(p.worst)}`}>{usd(p.worst)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {pages > 1 ? (
                <nav className="pager" aria-label="หน้าประวัติ Sto-H4">
                  {Array.from({ length: pages }, (_, i) => i + 1).map((n) => (
                    <button
                      key={n}
                      type="button"
                      className={`btn${n === current ? ' btn--primary' : ''}`}
                      aria-current={n === current ? 'page' : undefined}
                      onClick={() => setPage(n)}
                    >
                      {n}
                    </button>
                  ))}
                </nav>
              ) : null}
            </>
          )}
        </Panel>
      </div>
    </>
  );
}
